import { useEffect, useRef, useState, type ReactNode } from "react";
import { api, ApiError } from "@/lib/apiClient";
import type { User, UserRole } from "@/types/user";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { PasswordInput } from "@/components/PasswordInput";
import { useToast } from "@/hooks/useToast";

const ROLE_LABELS: Record<UserRole, string> = { user: "利用者", manager: "承認者", admin: "管理者" };
const ROLE_RANK: Record<UserRole, number> = { user: 0, manager: 1, admin: 2 };

type SortKey = "custom" | "displayName" | "email" | "department" | "role" | "hasPassword" | "lineLinked" | "active";

function sortValue(u: User, key: SortKey): string | number {
  switch (key) {
    case "custom":
      return 0;
    case "displayName":
      return u.displayName;
    case "email":
      return u.email;
    case "department":
      return u.department ?? "";
    case "role":
      return ROLE_RANK[u.role];
    case "hasPassword":
      return u.hasPassword ? 1 : 0;
    case "lineLinked":
      return u.lineLinked ? 1 : 0;
    case "active":
      return u.active ? 1 : 0;
  }
}

interface FormState {
  id: number | null;
  email: string;
  displayName: string;
  department: string;
  role: UserRole;
  password: string;
}

const EMPTY_FORM: FormState = {
  id: null,
  email: "",
  displayName: "",
  department: "",
  role: "user",
  password: "",
};

export function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "custom", dir: "asc" });
  const [reorderList, setReorderList] = useState<User[] | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const dragIndex = useRef<number | null>(null);
  const notify = useToast();

  const toggleSort = (key: SortKey) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  // "custom" = the saved drag-and-drop order (users is already returned in
  // that order by the API); any other key re-sorts client-side by that column.
  const sortedUsers =
    sort.key === "custom"
      ? users
      : [...users].sort((a, b) => {
          const av = sortValue(a, sort.key);
          const bv = sortValue(b, sort.key);
          const cmp =
            typeof av === "string" && typeof bv === "string" ? av.localeCompare(bv, "ja") : (av as number) - (bv as number);
          return sort.dir === "asc" ? cmp : -cmp;
        });

  const load = () => {
    setLoading(true);
    api
      .get<User[]>("/users")
      .then(setUsers)
      .catch((err) => setError(err instanceof ApiError ? err.message : "読み込みに失敗しました。"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const submit = async () => {
    if (!form || submitting) return;
    if (!form.email || !form.displayName) {
      setFormError("メールアドレスと氏名は必須です。");
      return;
    }
    if (form.password && form.password.length < 8) {
      setFormError("パスワードは8文字以上で入力してください。");
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      if (form.id) {
        await api.patch(`/users/${form.id}`, {
          displayName: form.displayName,
          department: form.department || null,
          role: form.role,
          password: form.password || undefined,
        });
      } else {
        await api.post("/users", {
          email: form.email,
          displayName: form.displayName,
          department: form.department || null,
          role: form.role,
          password: form.password || undefined,
        });
      }
      notify(form.id ? "使用者情報を更新しました。" : "使用者を登録しました。");
      setForm(null);
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "保存に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  const deactivate = async (user: User) => {
    if (!confirm(`${user.displayName} を無効化しますか？`)) return;
    await api.delete(`/users/${user.id}`);
    notify(`${user.displayName} を無効化しました。`);
    load();
  };

  const remove = async (user: User) => {
    if (!confirm(`${user.displayName} を完全に削除しますか？この操作は取り消せません。`)) return;
    try {
      await api.delete(`/users/${user.id}/permanent`);
      notify(`${user.displayName} を削除しました。`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "削除に失敗しました。");
    }
  };

  const startReorder = () => {
    setReorderList(users);
  };

  const cancelReorder = () => {
    setReorderList(null);
    dragIndex.current = null;
  };

  const moveRow = (targetIndex: number) => {
    if (dragIndex.current === null || !reorderList || dragIndex.current === targetIndex) return;
    const next = [...reorderList];
    const [moved] = next.splice(dragIndex.current, 1);
    next.splice(targetIndex, 0, moved);
    dragIndex.current = targetIndex;
    setReorderList(next);
  };

  const saveOrder = async () => {
    if (!reorderList) return;
    setSavingOrder(true);
    try {
      await api.patch("/users/reorder", { order: reorderList.map((u) => u.id) });
      notify("並び順を保存しました。");
      setReorderList(null);
      setSort({ key: "custom", dir: "asc" });
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "並び順の保存に失敗しました。");
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">使用者管理</h1>
      <p className="page-subtitle">使用者の登録・権限変更・無効化を行います。</p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => { setForm({ ...EMPTY_FORM }); setFormError(null); }}>
          新しい使用者を登録
        </button>
        {reorderList ? (
          <>
            <button className="btn btn-primary" onClick={saveOrder} disabled={savingOrder}>
              {savingOrder ? "保存中..." : "並び順を保存"}
            </button>
            <button className="btn btn-secondary" onClick={cancelReorder} disabled={savingOrder}>
              キャンセル
            </button>
          </>
        ) : (
          <button className="btn btn-secondary" onClick={startReorder}>
            並び替えモード
          </button>
        )}
        {!reorderList && sort.key !== "custom" && (
          <button className="btn btn-secondary" onClick={() => setSort({ key: "custom", dir: "asc" })}>
            保存した並び順に戻す
          </button>
        )}
      </div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <EmptyState message={error} />
      ) : reorderList ? (
        <div className="table-scroll">
          <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginTop: 0 }}>
            行をドラッグして好きな順番に並べ替え、「並び順を保存」を押してください。
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--color-border)" }}>
                <Th></Th>
                <Th>氏名</Th>
                <Th>メールアドレス</Th>
                <Th>権限</Th>
              </tr>
            </thead>
            <tbody>
              {reorderList.map((u, index) => (
                <tr
                  key={u.id}
                  draggable
                  onDragStart={() => (dragIndex.current = index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnter={() => moveRow(index)}
                  onDragEnd={() => (dragIndex.current = null)}
                  style={{
                    borderBottom: "1px solid var(--color-border)",
                    cursor: "grab",
                    background: "var(--color-surface)",
                  }}
                >
                  <Td>
                    <span style={{ color: "var(--color-text-muted)" }}>⠿</span>
                  </Td>
                  <Td>{u.displayName}</Td>
                  <Td>{u.email}</Td>
                  <Td>{ROLE_LABELS[u.role]}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--color-border)" }}>
                <SortableTh label="氏名" sortKey="displayName" sort={sort} onClick={toggleSort} />
                <SortableTh label="メールアドレス" sortKey="email" sort={sort} onClick={toggleSort} />
                <SortableTh label="部署" sortKey="department" sort={sort} onClick={toggleSort} />
                <SortableTh label="権限" sortKey="role" sort={sort} onClick={toggleSort} />
                <SortableTh label="パスワード" sortKey="hasPassword" sort={sort} onClick={toggleSort} />
                <SortableTh label="LINE連携" sortKey="lineLinked" sort={sort} onClick={toggleSort} />
                <SortableTh label="状態" sortKey="active" sort={sort} onClick={toggleSort} />
                <Th>操作</Th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => (
                <tr key={u.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <Td>{u.displayName}</Td>
                  <Td>{u.email}</Td>
                  <Td>{u.department ?? "-"}</Td>
                  <Td>{ROLE_LABELS[u.role]}</Td>
                  <Td>{u.hasPassword ? "設定済み" : "未設定"}</Td>
                  <Td>{u.lineLinked ? "連携済み" : "未連携"}</Td>
                  <Td>{u.active ? "有効" : "無効"}</Td>
                  <Td>
                    <button
                      className="btn btn-secondary"
                      style={{ height: 32, padding: "0 10px", marginRight: 8 }}
                      onClick={() => {
                        setForm({
                          id: u.id,
                          email: u.email,
                          displayName: u.displayName,
                          department: u.department ?? "",
                          role: u.role,
                          password: "",
                        });
                        setFormError(null);
                      }}
                    >
                      編集
                    </button>
                    {u.active && (
                      <button
                        className="btn btn-danger"
                        style={{ height: 32, padding: "0 10px", marginRight: 8 }}
                        onClick={() => deactivate(u)}
                      >
                        無効化
                      </button>
                    )}
                    <button className="btn btn-danger" style={{ height: 32, padding: "0 10px" }} onClick={() => remove(u)}>
                      削除
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <div className="modal-backdrop" onClick={() => setForm(null)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="section-title" style={{ margin: 0 }}>
                {form.id ? "使用者を編集" : "使用者を登録"}
              </h2>
              <button className="modal-close" onClick={() => setForm(null)}>
                ×
              </button>
            </div>

            <div className="form-field">
              <label>メールアドレス</label>
              <input type="email" value={form.email} disabled={form.id !== null} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="form-field">
              <label>氏名</label>
              <input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
            </div>
            <div className="form-field">
              <label>部署</label>
              <input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
            </div>
            <div className="form-field">
              <label>権限</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}>
                <option value="user">利用者</option>
                <option value="manager">承認者</option>
                <option value="admin">管理者</option>
              </select>
            </div>
            <div className="form-field">
              <label>{form.id ? "新しいパスワード（変更する場合のみ入力）" : "初期パスワード（8文字以上）"}</label>
              <PasswordInput
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={form.id ? "空欄のままなら変更なし" : ""}
              />
              <p style={{ fontSize: 12, color: "var(--color-text-muted)", margin: "4px 0 0" }}>
                このパスワードは本人にお伝えください（画面には再表示されません）。
              </p>
            </div>

            {formError && <p className="error-text">{formError}</p>}

            <button className="btn btn-primary btn-block" onClick={submit} disabled={submitting}>
              {submitting ? "保存中..." : "保存する"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children?: ReactNode }) {
  return <th style={{ padding: "10px 12px", fontSize: 12, color: "var(--color-text-muted)" }}>{children}</th>;
}

function SortableTh({
  label,
  sortKey,
  sort,
  onClick,
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: "asc" | "desc" };
  onClick: (key: SortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <th style={{ padding: 0, fontSize: 12, color: "var(--color-text-muted)" }}>
      <button
        onClick={() => onClick(sortKey)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          width: "100%",
          height: "100%",
          padding: "10px 12px",
          background: "none",
          border: "none",
          font: "inherit",
          color: active ? "var(--color-primary)" : "var(--color-text-muted)",
          fontWeight: active ? 700 : 400,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        <span style={{ fontSize: 10, opacity: active ? 1 : 0.3 }}>{active && sort.dir === "desc" ? "▼" : "▲"}</span>
      </button>
    </th>
  );
}
function Td({ children }: { children: ReactNode }) {
  return <td style={{ padding: "10px 12px", fontSize: 14 }}>{children}</td>;
}
